import type { PublicGameWithTeams } from '../../services/interfaces/propsService/api';
import type { TPropExpanded, TUserProp } from '../../services/interfaces/propsService/parsed/props';

export function teamIdForLeagueImage(
  prop: Pick<TPropExpanded | TUserProp, 'team_id' | 'team' | 'entity'>
): string | undefined {
  return prop.team_id ?? prop.team?.id ?? prop.entity?.current_team_id;
}

/**
 * Team id for CDN logo on pick rows. Prefer explicit prop fields; then lineup membership;
 * then match `prop.team.alias` to the game when the API omits `team_id` but includes team context.
 */
export function playerTeamIdForPick(
  prop: TPropExpanded | TUserProp,
  game: PublicGameWithTeams
): string {
  const direct = teamIdForLeagueImage(prop);
  if (direct) return direct;

  const entityId = prop.entity?.id ?? prop.entity_id;
  if (entityId) {
    if (game.home.lineup?.some((m) => m.id === entityId)) {
      return game.home.id;
    }
    if (game.away.lineup?.some((m) => m.id === entityId)) {
      return game.away.id;
    }
  }

  const alias = prop.team?.alias;
  if (alias) {
    if (game.home.alias === alias) {
      return game.home.id;
    }
    if (game.away.alias === alias) {
      return game.away.id;
    }
  }

  return prop.team_id ?? '';
}

export function formatGameDescriptionWithScores(
  gameDescription: string,
  homeAlias: string | undefined,
  awayAlias: string | undefined,
  homePoints: number,
  awayPoints: number
): string {
  if (!homeAlias || !awayAlias) return gameDescription;

  const isAtSep = gameDescription.includes(' @ ');
  const sepToken = isAtSep ? ' @ ' : ' vs. ';
  const parts = gameDescription.split(sepToken);
  if (parts.length !== 2) return gameDescription;

  const [leftRaw, rightRaw] = parts;
  const left = leftRaw.trim();
  const right = rightRaw.trim();
  if ((left !== homeAlias && left !== awayAlias) || (right !== homeAlias && right !== awayAlias)) {
    return gameDescription;
  }

  const sep = isAtSep ? '@' : 'vs.';
  const leftScore = left === homeAlias ? homePoints : awayPoints;
  const rightScore = right === homeAlias ? homePoints : awayPoints;

  return `${left} ${leftScore} ${sep} ${right} ${rightScore}`;
}
